import type { TileSet } from './tiles';
import { pairs, type WorldMapSpec } from './worldSpec';

/** Saltspray Cove's painting (approved 29 October), tiled as
 *  public/world/cove/v1 at WebP Q74. A changed painting goes in a new
 *  version folder, with its spots measured again. */
export const COVE_TILES: TileSet = {
  base: '/world/cove/v1/map_files',
  width: 3072,
  height: 2048,
  tileSize: 512,
  top: 3,
};

/**
 * Saltspray Cove, from the painting's spots.json. The outlines hug their
 * structures (so the water mask stays off them), so the lighthouse, the
 * stilt hut, the lagoon's rock, the pirate jetty, the wreck and the treasure
 * beach carry a padded `tap` shape a finger can hit; the Kraken's cave is
 * big enough to be tapped by its outline. The buildings stand among water,
 * rocks and trees with the road close below most of them, so every plate
 * hangs above its building (Jon, 26 October: beside or above, never painted
 * for), clear of the pieces' places. Mermaid Cove's plate is a narrow two-line one on the
 * smallest phone: a wide one cannot hang between the hut and the jetty.
 */
export const COVE: WorldMapSpec = {
  biome: 'cove',
  tiles: COVE_TILES,
  buildings: [
    {
      node: 8,
      name: 'Stormy Seas',
      centre: [250, 670],
      plate: [400, 280],
      plateTight: [450, 250],
      outline: pairs([
        180, 470, 300, 450, 316, 600, 330, 700, 400, 820, 410, 880, 260, 890, 130, 840, 160, 740,
        190, 640,
      ]),
      tap: pairs([
        151, 422, 124, 463, 133, 635, 75, 827, 80, 866, 113, 894, 248, 945, 439, 928, 466, 887, 457,
        817, 384, 679, 350, 424, 329, 402, 300, 394,
      ]),
    },
    {
      node: 9,
      name: 'Map Fragment',
      centre: [700, 750],
      plate: [700, 390],
      plateTight: [980, 370],
      outline: pairs([
        560, 560, 620, 550, 700, 590, 770, 660, 790, 740, 850, 740, 890, 790, 890, 880, 850, 930,
        780, 945, 700, 950, 600, 940, 540, 900, 530, 850, 500, 800, 500, 700, 520, 620,
      ]),
      tap: pairs([
        531, 519, 472, 604, 450, 691, 451, 812, 502, 933, 596, 990, 772, 996, 879, 971, 939, 892,
        940, 783, 926, 754, 879, 699, 828, 680, 808, 627, 725, 546, 631, 501,
      ]),
    },
    {
      node: 10,
      name: 'Mermaid Cove',
      centre: [1100, 1080],
      plate: [1115, 822],
      plateTight: [1110, 845],
      plateNarrow: true,
      outline: pairs([
        990, 1060, 1010, 1010, 1060, 995, 1140, 995, 1190, 1020, 1205, 1070, 1190, 1120, 1130, 1140,
        1040, 1140, 1000, 1110,
      ]),
      tap: pairs([
        961, 975, 930, 1052, 940, 1120, 954, 1149, 1008, 1191, 1142, 1199, 1210, 1177, 1243, 1149,
        1265, 1062, 1246, 997, 1221, 968, 1152, 936, 1059, 935, 990, 953,
      ]),
    },
    {
      node: 11,
      name: 'Pirate Plunder',
      centre: [1490, 870],
      plate: [1490, 470],
      plateTight: [1630, 440],
      outline: pairs([
        1415, 630, 1470, 640, 1560, 700, 1565, 770, 1640, 830, 1685, 880, 1685, 930, 1665, 1010,
        1650, 1060, 1570, 1110, 1500, 1110, 1440, 1040, 1420, 960, 1335, 900, 1330, 800, 1345, 740,
        1360, 700, 1405, 660,
      ]),
      tap: pairs([
        1401, 582, 1322, 667, 1280, 791, 1286, 912, 1302, 938, 1375, 992, 1397, 1066, 1466, 1147,
        1500, 1160, 1584, 1158, 1693, 1086, 1735, 938, 1734, 868, 1682, 801, 1616, 745, 1609, 688,
        1593, 662, 1490, 594,
      ]),
    },
    {
      node: 12,
      name: 'Davy Jones',
      centre: [1980, 900],
      plate: [1980, 470],
      plateTight: [2160, 450],
      outline: pairs([
        2024, 636, 2045, 650, 2075, 740, 2086, 800, 2070, 830, 2040, 870, 1990, 930, 1950, 990,
        1900, 990, 1850, 940, 1815, 880, 1797, 800, 1810, 775, 1890, 750, 1960, 700,
      ]),
      tap: pairs([
        2044, 590, 1998, 593, 1870, 703, 1784, 732, 1750, 782, 1747, 809, 1775, 912, 1811, 972,
        1880, 1036, 1950, 1040, 1984, 1027, 2133, 818, 2125, 731, 2093, 634,
      ]),
    },
    {
      node: 13,
      name: "Kraken's Lair",
      centre: [2450, 960],
      plate: [2450, 570],
      plateTight: [2450, 310],
      outline: pairs([
        2250, 860, 2320, 750, 2420, 690, 2560, 700, 2640, 800, 2680, 940, 2660, 1060, 2620, 1140,
        2500, 1190, 2340, 1180, 2240, 1120, 2210, 1000,
      ]),
    },
    {
      node: 14,
      name: 'Buried Treasure',
      centre: [2880, 960],
      plate: [2670, 460],
      plateTight: [2800, 520],
      outline: pairs([
        2770, 840, 2820, 720, 2940, 710, 3040, 790, 3050, 900, 3030, 1060, 2980, 1160, 2860, 1165,
        2770, 1150, 2720, 1060, 2730, 940,
      ]),
      tap: pairs([
        2960, 664, 2817, 670, 2786, 683, 2683, 921, 2671, 1072, 2726, 1175, 2750, 1196, 2858, 1215,
        2994, 1208, 3018, 1193, 3053, 1125, 3071, 1115, 3071, 745,
      ]),
    },
  ],
  road: pairs([
    0, 964, 120, 956, 200, 980, 300, 1040, 400, 1054, 500, 1066, 600, 1090, 700, 1130, 800, 1230,
    900, 1296, 1040, 1328, 1200, 1300, 1330, 1244, 1480, 1192, 1600, 1200, 1680, 1240, 1880, 1264,
    2040, 1234, 2180, 1206, 2380, 1224, 2530, 1290, 2680, 1350, 2830, 1360, 2980, 1310, 3072, 1264,
  ]),
  roadWidth: 80,
  still: [
    {
      name: 'boardwalk at the left edge',
      outline: pairs([0, 936, 110, 910, 190, 924, 200, 980, 100, 990, 0, 1000]),
    },
    {
      name: 'mapmaker jetty deck',
      outline: pairs([530, 810, 660, 760, 860, 790, 890, 860, 800, 910, 660, 940, 570, 890]),
    },
    {
      name: 'pirate jetty deck and piers',
      outline: pairs([
        1336, 800, 1580, 840, 1690, 940, 1680, 1040, 1580, 1090, 1490, 1080, 1400, 1060, 1336, 960,
      ]),
    },
    {
      name: 'wreck ramp',
      outline: pairs([
        1930, 925, 1990, 935, 2090, 1000, 2085, 1066, 2040, 1070, 1980, 1020, 1930, 960,
      ]),
    },
    {
      name: 'wreck broken deck',
      outline: pairs([2062, 872, 2135, 868, 2192, 905, 2195, 990, 2090, 992, 2062, 960]),
    },
    {
      name: 'wreck crate',
      outline: pairs([1812, 964, 1850, 958, 1890, 975, 1890, 1038, 1815, 1040]),
    },
    {
      name: 'glowing chest',
      outline: pairs([1880, 1036, 1975, 1036, 1978, 1112, 1885, 1115]),
    },
    {
      name: 'footbridge at the top',
      outline: pairs([1940, 190, 2020, 170, 2150, 170, 2170, 210, 2080, 250, 1960, 236]),
    },
  ],
  // The painting has no lamps of its own, and its verges are rocks, bushes and
  // shallows, so the posts the code draws stand where each was proved on open
  // grass or dirt beside the path (a 2x crop at every foot, 29 October): off
  // the sea and trees, rocks, buildings, plates and still areas.
  lanternPosts: pairs([
    397, 1106, 602, 1037, 977, 1367, 1122, 1403, 1285, 1320, 1717, 1351, 1841, 1324, 2001, 1294,
    2169, 1261, 2322, 1283, 2444, 1309, 2625, 1384, 2808, 1411,
  ]),
  plateBox: [420, 110],
  plateBoxTight: [528, 138],
  plateBoxNarrow: [394, 138],
  ambient: '/world/cove/ambient/v1',
  ambientFiles: ['life.json', 'life.webp', 'sway.webp', 'water.webp'],
  // The Cove's road friends' kinds: no starfish, crab, dolphin, oyster or
  // pearl in its life (seals, pelicans and a whale's spout stand in).
  forbidden: ['starfish', 'crab', 'dolphin', 'oyster', 'pearl'],
};
